/**
 * Hook para gerenciar progressbar híbrida (simulada + real).
 *
 * COMPORTAMENTO:
 * - Simula progresso quando operação é rápida demais
 * - Usa progresso real do C# quando disponível
 * - Garante tempo mínimo de exibição para cada step
 * - Transição suave entre steps com animação
 * - Delay após 100% antes de fechar
 *
 * USO:
 * ```tsx
 * const { progress, step, updateRealProgress, completeStep, reset, isComplete } = useSimulatedProgress();
 *
 * // No callback da pipeline:
 * onProgress: (event) => {
 *   if (event.stage === 'rules:loading') completeStep('rules');
 *   if (event.stage === 'collect:progress') updateRealProgress(event.meta);
 * }
 *
 * // Aguardar conclusão antes de mudar de tela:
 * if (isComplete) setPageState('confirmMaterial');
 * ```
 */

import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Configuração da progressbar
 */
export const PROGRESS_CONFIG = {
  /** Tempo mínimo total de exibição (ms) */
  MIN_DISPLAY_TIME_MS: 1200,
  /** Tempo mínimo por step (ms) */
  MIN_STEP_TIME_MS: 350,
  /** Delay após 100% antes de marcar como completo (ms) */
  CLOSE_DELAY_MS: 300,
  /** Intervalo de atualização da animação (ms) */
  ANIMATION_INTERVAL_MS: 50,
  /** Velocidade de incremento do progresso simulado (% por intervalo) */
  SIMULATED_INCREMENT: 2,
};

/**
 * Definição de um step da progressbar
 */
export interface ProgressStep {
  id: string;
  label: string;
  description: string;
  /** Peso do step no progresso total (0-100) */
  weight: number;
}

/**
 * Steps padrão para o fluxo de orçamento
 */
export const QUOTATION_STEPS: ProgressStep[] = [
  {
    id: 'rules',
    label: 'Carregando materiais',
    description: 'Buscando regras de produtos...',
    weight: 25,
  },
  {
    id: 'collect',
    label: 'Coletando objetos',
    description: 'Coletando objetos da seleção...',
    weight: 50,
  },
  {
    id: 'detect',
    label: 'Detectando material',
    description: 'Analisando dimensões...',
    weight: 25,
  },
];

/**
 * Estado do progresso detalhado (vindo do C#)
 */
export interface DetailedProgress {
  percentComplete: number;
  message: string;
  current: number;
  total: number;
}

/**
 * Retorno do hook
 */
export interface UseSimulatedProgressResult {
  /** Progresso global (0-100) */
  progress: number;
  /** Step atual */
  currentStep: string;
  /** Índice do step atual */
  currentStepIndex: number;
  /** Progresso detalhado (do C# ou simulado) */
  detailedProgress: DetailedProgress | null;
  /** Se a progressbar está completa (100% + delay) */
  isComplete: boolean;
  /** Se está aguardando delay de fechamento */
  isClosing: boolean;
  /** Atualiza com progresso real do C# */
  updateRealProgress: (progress: Partial<DetailedProgress>) => void;
  /** Marca um step como concluído e avança */
  completeStep: (stepId: string) => void;
  /** Define o step atual manualmente */
  setStep: (stepId: string) => void;
  /** Reseta todo o progresso */
  reset: () => void;
  /** Força conclusão imediata (para erros) */
  forceComplete: () => void;
}

/**
 * Hook para progressbar híbrida
 */
export function useSimulatedProgress(
  steps: ProgressStep[] = QUOTATION_STEPS
): UseSimulatedProgressResult {
  // Estado
  const [progress, setProgress] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [detailedProgress, setDetailedProgress] = useState<DetailedProgress | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Refs para controle de timing
  const startTimeRef = useRef<number>(Date.now());
  const stepStartTimeRef = useRef<number>(Date.now());
  const animationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRealProgressRef = useRef<boolean>(false);
  const completedStepsRef = useRef<Set<string>>(new Set());
  const pendingResultRef = useRef<(() => void) | null>(null);
  // ✅ CORREÇÃO: Ref para rastrear o progresso máximo atingido (nunca diminuir)
  const maxProgressReachedRef = useRef<number>(0);

  /**
   * Atualiza o progresso garantindo que nunca diminua
   * Sempre mantém o maior valor entre o progresso atual e o novo valor
   */
  const setProgressSafe = useCallback((newProgress: number | ((prev: number) => number)) => {
    setProgress((prev) => {
      const nextValue = typeof newProgress === 'function' ? newProgress(prev) : newProgress;
      // Sempre usar o maior valor entre: progresso atual, novo valor, e máximo já atingido
      const safeProgress = Math.max(prev, nextValue, maxProgressReachedRef.current);
      // Atualizar o ref com o maior valor
      maxProgressReachedRef.current = safeProgress;
      return safeProgress;
    });
  }, []);

  // Step atual
  const currentStep = steps[currentStepIndex]?.id || 'rules';

  // Calcular progresso base do step atual
  const calculateBaseProgress = useCallback((stepIndex: number): number => {
    let base = 0;
    for (let i = 0; i < stepIndex; i++) {
      base += steps[i]?.weight || 0;
    }
    return base;
  }, [steps]);

  // Iniciar animação de progresso simulado
  const startSimulation = useCallback(() => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
    }

    animationIntervalRef.current = setInterval(() => {
      // Não simular se temos progresso real
      if (hasRealProgressRef.current) return;

      setProgressSafe((prev) => {
        // Calcular máximo permitido para o step atual
        const baseProgress = calculateBaseProgress(currentStepIndex);
        const stepWeight = steps[currentStepIndex]?.weight || 33;
        const maxForStep = baseProgress + stepWeight * 0.9; // Não passar de 90% do step

        // ✅ CRÍTICO: Só incrementar se ainda não chegou no máximo do step
        // O setProgressSafe já garante que nunca diminui
        if (prev < maxForStep) {
          const newProgress = Math.min(prev + PROGRESS_CONFIG.SIMULATED_INCREMENT, maxForStep);
          return newProgress;
        }

        // Se já passou do máximo do step, manter o valor atual (nunca diminuir)
        return prev;
      });
    }, PROGRESS_CONFIG.ANIMATION_INTERVAL_MS);
  }, [currentStepIndex, calculateBaseProgress, steps, setProgressSafe]);

  // Parar animação
  const stopSimulation = useCallback(() => {
    if (animationIntervalRef.current) {
      clearInterval(animationIntervalRef.current);
      animationIntervalRef.current = null;
    }
  }, []);

  // Atualizar com progresso real do C#
  const updateRealProgress = useCallback((realProgress: Partial<DetailedProgress>) => {
    hasRealProgressRef.current = true;

    const newProgress: DetailedProgress = {
      percentComplete: realProgress.percentComplete ?? 0,
      message: realProgress.message ?? 'Processando...',
      current: realProgress.current ?? 0,
      total: realProgress.total ?? 0,
    };

    setDetailedProgress(newProgress);

    // Calcular progresso global baseado no step atual
    const baseProgress = calculateBaseProgress(currentStepIndex);
    const stepWeight = steps[currentStepIndex]?.weight || 33;
    const stepProgress = (newProgress.percentComplete / 100) * stepWeight;

    // ✅ CORREÇÃO: Usar setProgressSafe para garantir que nunca diminua
    setProgressSafe(baseProgress + stepProgress);
  }, [currentStepIndex, calculateBaseProgress, steps, setProgressSafe]);

  // Completar um step
  const completeStep = useCallback((stepId: string) => {
    // Evitar completar o mesmo step duas vezes
    if (completedStepsRef.current.has(stepId)) return;
    completedStepsRef.current.add(stepId);

    const stepIndex = steps.findIndex((s) => s.id === stepId);
    if (stepIndex === -1) return;

    // Garantir tempo mínimo do step
    const elapsed = Date.now() - stepStartTimeRef.current;
    const remaining = Math.max(0, PROGRESS_CONFIG.MIN_STEP_TIME_MS - elapsed);

    const finishStep = () => {
      // Atualizar progresso para 100% do step
      const baseProgress = calculateBaseProgress(stepIndex + 1);
      // ✅ CORREÇÃO: Usar setProgressSafe para garantir que nunca diminua
      setProgressSafe(baseProgress);

      // Limpar progresso detalhado
      setDetailedProgress(null);
      hasRealProgressRef.current = false;

      // Avançar para próximo step se houver
      if (stepIndex < steps.length - 1) {
        setCurrentStepIndex(stepIndex + 1);
        stepStartTimeRef.current = Date.now();
      } else {
        // Último step - iniciar fechamento
        // ✅ CORREÇÃO: Usar setProgressSafe para garantir que nunca diminua
        setProgressSafe(100);
        setIsClosing(true);

        // Aguardar delay de fechamento
        setTimeout(() => {
          setIsComplete(true);
          // Se há callback pendente, executar
          if (pendingResultRef.current) {
            pendingResultRef.current();
            pendingResultRef.current = null;
          }
        }, PROGRESS_CONFIG.CLOSE_DELAY_MS);
      }
    };

    if (remaining > 0) {
      setTimeout(finishStep, remaining);
    } else {
      finishStep();
    }
  }, [steps, calculateBaseProgress, setProgressSafe]);

  // Definir step manualmente
  const setStep = useCallback((stepId: string) => {
    const stepIndex = steps.findIndex((s) => s.id === stepId);
    if (stepIndex !== -1 && stepIndex !== currentStepIndex) {
      setCurrentStepIndex(stepIndex);
      stepStartTimeRef.current = Date.now();
      hasRealProgressRef.current = false;
      setDetailedProgress(null);

      // ✅ CORREÇÃO: Usar setProgressSafe para garantir que nunca diminua
      const baseProgress = calculateBaseProgress(stepIndex);
      setProgressSafe((prev) => Math.max(prev, baseProgress));
    }
  }, [steps, currentStepIndex, calculateBaseProgress, setProgressSafe]);

  // Reset
  const reset = useCallback(() => {
    stopSimulation();
    // ✅ CORREÇÃO: Resetar também o maxProgressReachedRef
    maxProgressReachedRef.current = 0;
    setProgress(0);
    setCurrentStepIndex(0);
    setDetailedProgress(null);
    setIsComplete(false);
    setIsClosing(false);
    startTimeRef.current = Date.now();
    stepStartTimeRef.current = Date.now();
    hasRealProgressRef.current = false;
    completedStepsRef.current.clear();
    pendingResultRef.current = null;
  }, [stopSimulation]);

  // Forçar conclusão (para erros ou cancelamento)
  const forceComplete = useCallback(() => {
    stopSimulation();
    setIsComplete(true);
    setIsClosing(false);
  }, [stopSimulation]);

  // Iniciar simulação ao montar ou mudar de step
  useEffect(() => {
    if (!isComplete && !isClosing) {
      startSimulation();
    }
    return () => stopSimulation();
  }, [currentStepIndex, isComplete, isClosing, startSimulation, stopSimulation]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      stopSimulation();
    };
  }, [stopSimulation]);

  return {
    progress,
    currentStep,
    currentStepIndex,
    detailedProgress,
    isComplete,
    isClosing,
    updateRealProgress,
    completeStep,
    setStep,
    reset,
    forceComplete,
  };
}

/**
 * Utilitário para aguardar progressbar antes de continuar
 */
export function waitForMinDisplayTime(
  startTime: number,
  callback: () => void
): void {
  const elapsed = Date.now() - startTime;
  const remaining = Math.max(0, PROGRESS_CONFIG.MIN_DISPLAY_TIME_MS - elapsed);

  if (remaining > 0) {
    setTimeout(callback, remaining);
  } else {
    callback();
  }
}
